import React from 'react';
import { MODE_NAMES } from '../../lib/theory';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import {
  Tray, ControlCard, TrayLabel, KeyRows, StringsCard, StandardQuickButtons,
} from '../shared';

// ── Data ──────────────────────────────────────────────────────────────────────
// Only display labels live here. The option VALUES are the state values App.js and the
// SEO deep links use ('Full Scale', 'Pentatonic', 'Modes', 'Chords', 'F Shape', ...).

/** Structure labels: board 18 (phone) uses the short ones, board 20 (desktop) spells them out. */
const STRUCTURE_OPTIONS_PHONE = [
  { value: 'Full Scale', label: 'Scale' },
  { value: 'Pentatonic', label: 'Penta' },
  { value: 'Modes', label: 'Modes' },
  { value: 'Chords', label: 'Chords' },
];
const STRUCTURE_OPTIONS_DESKTOP = [
  { value: 'Full Scale', label: 'Scale' },
  { value: 'Pentatonic', label: 'Pentatonic' },
  { value: 'Modes', label: 'Modes' },
  { value: 'Chords', label: 'Chords' },
];

/** Chord shape labels: the state values keep their "Shape" suffix. */
const SHAPE_OPTIONS = [
  { value: 'F Shape', label: 'F' },
  { value: 'D Shape', label: 'D' },
  { value: 'Barre Shape', label: 'Barre' },
];

/** Strings in banjoStringFocus order: 1st (top of the board) to 4th, then the short 5th string. */
const STRINGS = [
  { label: 'D', ariaLabel: '1st string D' },
  { label: 'B', ariaLabel: '2nd string B' },
  { label: 'G', ariaLabel: '3rd string G' },
  { label: 'D', ariaLabel: '4th string D (lowest)' },
  { label: 'g', ariaLabel: '5th string (short) g' },
];

// ── Controls: Key card + Strings card ─────────────────────────────────────────

/**
 * Banjo controls (board 18 phone / board 20 desktop): the Key and Strings cards.
 * The quick buttons live in {@link BanjoQuickButtons}; the page layout puts them under the cards.
 * Modes opens a mode tray; Chords opens a Shape tray (F / D / Barre).
 */
export default function BanjoControls({
  banjoKey,         onBanjoKeyChange,
  banjoTonality,    onBanjoTonalityChange,
  banjoStructure,   onBanjoStructureChange,
  banjoMode,        onBanjoModeChange,
  banjoShape,       onBanjoShapeChange,
  banjoStringFocus, onBanjoStringToggle,
}) {
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 'md' : 'sm';
  const isModes = banjoStructure === 'Modes';
  const isChords = banjoStructure === 'Chords';

  return (
    <>
      <ControlCard title="Key" size={size}>
        <KeyRows
          isDesktop={isDesktop}
          keyValue={banjoKey}
          onKeyChange={onBanjoKeyChange}
          tonality={banjoTonality}
          onTonalityChange={onBanjoTonalityChange}
          // A mode sets its own quality, so Major/Minor sits out while Modes is on.
          tonalityDisabled={isModes}
          structureOptions={STRUCTURE_OPTIONS_PHONE}
          structureOptionsDesktop={STRUCTURE_OPTIONS_DESKTOP}
          structure={banjoStructure}
          onStructureChange={onBanjoStructureChange}
        />

        {isModes && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Mode</TrayLabel>}
            <Tray
              ariaLabel="Mode"
              options={MODE_NAMES}
              value={banjoMode}
              onChange={onBanjoModeChange}
              columns={isDesktop ? 3 : 4}
            />
          </>
        )}

        {isChords && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Shape</TrayLabel>}
            <Tray
              ariaLabel="Chord shape"
              options={SHAPE_OPTIONS}
              value={banjoShape}
              onChange={onBanjoShapeChange}
              columns={3}
            />
          </>
        )}
      </ControlCard>

      <StringsCard strings={STRINGS} focus={banjoStringFocus} onToggle={onBanjoStringToggle} size={size} />
    </>
  );
}

// ── Quick buttons: Filter / Labels / Reset ────────────────────────────────────

/**
 * The three quick buttons for the layout's `quickButtons` slot.
 * Filter cycles All Notes -> Triads Only -> Roots Only and is greyed for Chords (every chord tone
 * always shows). Labels toggles Note Names <-> Intervals ('None' counts as Names).
 */
export function BanjoQuickButtons({
  banjoStructure,
  banjoFilter, onBanjoFilterChange,
  banjoLabel,  onBanjoLabelChange,
  onBanjoReset,
}) {
  const isDesktop = useIsDesktop();
  return (
    <StandardQuickButtons
      size={isDesktop ? 'md' : 'sm'}
      filter={banjoFilter}
      onFilterChange={onBanjoFilterChange}
      filterDisabled={banjoStructure === 'Chords'}
      label={banjoLabel}
      onLabelChange={onBanjoLabelChange}
      onReset={onBanjoReset}
    />
  );
}
